import React, { useMemo } from 'react';
import {
    isEmbeddableLinksEnabled,
    isHelpSeekerCheckInExternalCustomerLockoutFilterEnabled,
    isEmailVerifiedSignalEnabled,
    isHelpdeskCreationEnabledOnHelpCenter,
    isUnifiedHelpReportingPageEnabled,
    isUnifiedHelpInJSMEnabled,
} from 'feature-flags';
import { RouteComponent, Router } from 'react-resource-router';
import { viewHomepageRoute, editHomepageRoute } from 'routes/advanced-help-center';
import { approvalsRoute } from 'routes/approvals';
import { PROJECT_NAME } from 'routes/common/constants';
import { viewConversationPageRoute } from 'routes/conversation';
import { editTopicRoute, addTopicRoute } from 'routes/edit-topic';
import { embedCreateRequestRoute, embedSinglePortalRoute, embedKBArticleRoute } from 'routes/embed-smartlinks';
import { helpCenterRouteWithResource } from 'routes/help-center';
import {
    viewAllSearchResultsRoute,
    viewPortalAllSearchResultsRoute,
    viewArticleRoute,
    viewKbPageRoute,
    viewPortalArticleRoute,
    viewPortalCategoryArticleRoute,
    viewPortalKnowledgeCategoryRoute,
} from 'routes/kb';
import { loginRoute, portalLoginRoute } from 'routes/login';
import { portalAccessForbiddenRoute } from 'routes/portal-access-forbidden';
import { portalHomeRoute } from 'routes/portal-home';
import { requestApprovalRoute } from 'routes/request-approval';
import { requestDetailsRoute } from 'routes/request-details';
import { requestsListRoute } from 'routes/requests';

import {
    portalInvitedSignupRoute,
    portalCompleteSignupRoute,
    portalResetPasswordRoute,
    portalSignupRoute,
    userCheckMailRoute,
    userCompleteSignupRoute,
    userResetPasswordRoute,
    userSignupRoute,
} from 'routes/signup';
import { viewTopicRoute } from 'routes/topics';
import {
    addHelpContentRoute,
    addHelpDeskRoute,
    addHelpResourcesRoute,
    editHelpDeskRoute,
    viewHelpDeskRoute,
} from 'routes/unified-help-center';
import { userVerifyEmailRoute } from 'routes/verify-email';
import { GlobalStyled } from 'view/styles/global';
import { isAdvancedHelpCenter } from '@atlassian/help-center-common-util/advanced-help-center';
import { history } from '@atlassian/help-center-common-util/history';
import { getCloudId, getHelpCenterAri } from '@atlassian/help-center-common-util/meta';
import {
    requestFeedbackRoute,
    unsubscribeRoute,
    userProfileRoute,
    helpReportingRoute,
    anonymousRequestSuccessRoute,
    requestCreateRoute,
    portalReorderRoute,
    notFoundErrorRoute,
} from '../routes';

export const JsmResourceRouter = () => {
    //Order matters here
    const routes = useMemo(
        () => [
            ...(isAdvancedHelpCenter() ? [viewHomepageRoute, editHomepageRoute] : []),
            ...(isEmailVerifiedSignalEnabled() ? [userVerifyEmailRoute] : []),
            loginRoute,
            userResetPasswordRoute,
            userSignupRoute,
            userCompleteSignupRoute,
            userCheckMailRoute,
            ...(isHelpSeekerCheckInExternalCustomerLockoutFilterEnabled() ? [portalAccessForbiddenRoute] : []),
            portalLoginRoute,
            portalSignupRoute,
            portalResetPasswordRoute,
            portalCompleteSignupRoute,
            portalInvitedSignupRoute,
            requestFeedbackRoute,
            unsubscribeRoute,
            helpCenterRouteWithResource,
            requestDetailsRoute,
            viewKbPageRoute,
            viewAllSearchResultsRoute,
            viewPortalAllSearchResultsRoute,
            viewPortalCategoryArticleRoute,
            viewPortalArticleRoute,
            viewArticleRoute,
            requestsListRoute,
            approvalsRoute,
            requestApprovalRoute,
            userProfileRoute,
            ...(isUnifiedHelpReportingPageEnabled() ? [helpReportingRoute] : []),
            viewPortalKnowledgeCategoryRoute,
            anonymousRequestSuccessRoute,
            requestCreateRoute,
            portalHomeRoute,
            ...(isEmbeddableLinksEnabled()
                ? [embedCreateRequestRoute, embedSinglePortalRoute, embedKBArticleRoute]
                : []),
            portalReorderRoute(true),
            editTopicRoute,
            addTopicRoute,
            viewTopicRoute,
            ...(isHelpdeskCreationEnabledOnHelpCenter()
                ? [addHelpDeskRoute, addHelpResourcesRoute, viewHelpDeskRoute, editHelpDeskRoute, addHelpContentRoute]
                : []),
            ...(isUnifiedHelpInJSMEnabled() ? [viewConversationPageRoute] : []),
            notFoundErrorRoute,
        ],
        []
    );
    const customContext = {
        cloudId: getCloudId(),
        productName: PROJECT_NAME,
        helpCenterAri: getHelpCenterAri(),
    };

    //isGlobal=false, to avoid the router conflicting with the sidebar's router.
    return (
        <Router routes={routes} history={history} isGlobal={false} resourceContext={customContext}>
            <GlobalStyled />
            <RouteComponent />
        </Router>
    );
};

export default JsmResourceRouter;
